import MAP_RULES from "./mapRules.json";

export { MAP_RULES };

export const DISTRICT_COLORS = ["#4DB8B5", "#C084FC", "#F59E0B", "#60A5FA", "#F87171", "#34D399"];

const MODELS_BY_ID = new Map((MAP_RULES.islandModels || []).map((m) => [m.id, m]));

export function getIslandModel(islandModelId) {
  return MODELS_BY_ID.get(islandModelId) || MAP_RULES.defaultModel;
}

export const MAX_BUILDINGS_PER_ISLAND = Math.max(
  ...(MAP_RULES.islandModels || []).map((m) => m.buildingSlots || 0),
  MAP_RULES.defaultModel?.buildingSlots || 0
);

function hashString(value) {
  let h = 0;
  const s = String(value || "");
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

export function pickIslandModelId(islandKey, buildingCount) {
  const models = MAP_RULES.islandModels || [];
  if (!models.length) return MAP_RULES.defaultModel?.id;
  const fitting = models.filter((m) => m.buildingSlots >= buildingCount);
  if (!fitting.length) {
    return models.reduce((a, b) => (b.buildingSlots > a.buildingSlots ? b : a)).id;
  }
  const minSlots = Math.min(...fitting.map((m) => m.buildingSlots));
  const pool = fitting.filter((m) => m.buildingSlots === minSlots);
  return pool[hashString(islandKey) % pool.length].id;
}

export function resolveIslandModelId(island) {
  const count = (island?.buildings || []).length;
  const current = MODELS_BY_ID.get(island?.island_id);
  if (current && current.buildingSlots >= count) return current.id;
  return pickIslandModelId(island?.id || island?.name, count);
}

function uniqueIslandId(baseId, takenIds) {
  let n = 2;
  while (takenIds.has(`${baseId}-${n}`)) n += 1;
  const id = `${baseId}-${n}`;
  takenIds.add(id);
  return id;
}

export function splitOversizedIslands(districts) {
  const max = MAX_BUILDINGS_PER_ISLAND;
  if (!max) return districts;
  const takenIds = new Set();
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      if (island?.id) takenIds.add(island.id);
    }
  }

  for (const district of districts || []) {
    const next = [];
    for (const island of district.islands || []) {
      next.push(island);
      const buildings = island?.buildings || [];
      if (buildings.length <= max) continue;
      island.buildings = buildings.slice(0, max);
      let part = 2;
      for (let i = max; i < buildings.length; i += max) {
        const { layout, island_id, is_unlocked, ...rest } = island;
        next.push({
          ...rest,
          id: uniqueIslandId(island.id, takenIds),
          name: `${island.name || "Island"} ${part}`,
          buildings: buildings.slice(i, i + max),
        });
        part += 1;
      }
    }
    district.islands = next;
  }
  return districts;
}

export function applyIslandModels(districts) {
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      if (island) island.island_id = resolveIslandModelId(island);
    }
  }
  return districts;
}

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function assignDistrictColors(districts) {
  const used = new Set(
    (districts || []).map((d) => d?.color).filter((c) => HEX_COLOR.test(c || "")).map((c) => c.toUpperCase())
  );
  let index = 0;
  for (const district of districts || []) {
    if (!district || HEX_COLOR.test(district.color || "")) continue;
    const free = DISTRICT_COLORS.find((c) => !used.has(c.toUpperCase()));
    const color = free || DISTRICT_COLORS[index % DISTRICT_COLORS.length];
    district.color = color;
    used.add(color.toUpperCase());
    index += 1;
  }
  return districts;
}

function finite(value) {
  return typeof value === "number" && Number.isFinite(value);
}

export function isValidDistrictLayout(layout) {
  return Boolean(
    layout && finite(layout.x) && finite(layout.z) && finite(layout.width) && finite(layout.depth) &&
    layout.width > 0 && layout.depth > 0
  );
}

export function isValidIslandLayout(layout) {
  return Boolean(layout && finite(layout.x) && finite(layout.z));
}

export function cleanLayouts(districts) {
  for (const district of districts || []) {
    if (!district) continue;
    if (district.layout != null && !isValidDistrictLayout(district.layout)) delete district.layout;
    for (const island of district.islands || []) {
      if (island?.layout != null && !isValidIslandLayout(island.layout)) delete island.layout;
    }
  }
  return districts;
}

export function islandPolygon(islandModelId, layout) {
  if (!isValidIslandLayout(layout)) return null;
  const theta = ((layout.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return (getIslandModel(islandModelId)?.outline || []).map(([x, z]) => [
    layout.x + x * cos + z * sin,
    layout.z - x * sin + z * cos,
  ]);
}

export function applyLayoutCorrection(districts, correction) {
  const districtFixes = new Map();
  for (const d of correction?.districts || []) {
    if (d?.id == null) continue;
    const layout = { x: d.x, z: d.z, width: d.width, depth: d.depth };
    if (isValidDistrictLayout(layout)) districtFixes.set(String(d.id), layout);
  }
  const islandFixes = new Map();
  for (const i of correction?.islands || []) {
    if (i?.id == null) continue;
    const layout = { x: i.x, z: i.z, rotation: finite(i.rotation) ? i.rotation : 0 };
    if (isValidIslandLayout(layout)) islandFixes.set(String(i.id), layout);
  }
  if (!districtFixes.size && !islandFixes.size) return districts;

  let changed = false;
  const next = (districts || []).map((district) => {
    let updated = district;
    const districtLayout = districtFixes.get(String(district.id));
    if (districtLayout) {
      updated = { ...updated, layout: districtLayout };
      changed = true;
    }
    if ((district.islands || []).some((i) => islandFixes.has(String(i.id)))) {
      updated = {
        ...updated,
        islands: district.islands.map((island) => {
          const islandLayout = islandFixes.get(String(island.id));
          return islandLayout ? { ...island, layout: islandLayout } : island;
        }),
      };
      changed = true;
    }
    return updated;
  });
  return changed ? next : districts;
}

const FALLBACK_DISTRICT_SIZE = { width: 320, depth: 260 };

export function displayLayouts(districts) {
  const list = districts || [];
  const result = { districts: new Map(), islands: new Map() };
  const missing = list.filter((d) => !isValidDistrictLayout(d.layout));
  const ringRadius =
    (MAP_RULES.havenExclusionRadius || 0) + (MAP_RULES.districtSpacing || 0) +
    Math.hypot(FALLBACK_DISTRICT_SIZE.width, FALLBACK_DISTRICT_SIZE.depth) / 2 + 40;

  for (const district of list) {
    let layout = district.layout;
    if (!isValidDistrictLayout(layout)) {
      const k = missing.indexOf(district);
      const angle = (k / Math.max(missing.length, 1)) * Math.PI * 2 + Math.PI / 4;
      layout = {
        x: Math.round(Math.cos(angle) * ringRadius * (1 + Math.floor(k / 6) * 0.9)),
        z: Math.round(Math.sin(angle) * ringRadius * (1 + Math.floor(k / 6) * 0.9)),
        ...FALLBACK_DISTRICT_SIZE,
      };
    }
    result.districts.set(district.id, layout);

    const islands = district.islands || [];
    const unplaced = islands.filter((i) => !isValidIslandLayout(i.layout));
    const cols = Math.max(1, Math.ceil(Math.sqrt(unplaced.length)));
    const rows = Math.max(1, Math.ceil(unplaced.length / cols));
    for (const island of islands) {
      if (isValidIslandLayout(island.layout)) {
        result.islands.set(island.id, { rotation: 0, ...island.layout });
        continue;
      }
      const k = unplaced.indexOf(island);
      result.islands.set(island.id, {
        x: layout.x - layout.width / 2 + ((k % cols) + 0.5) * (layout.width / cols),
        z: layout.z + layout.depth / 2 - (Math.floor(k / cols) + 0.5) * (layout.depth / rows),
        rotation: 0,
      });
    }
  }
  return result;
}

export function pointInPolygon([x, z], polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, zi] = polygon[i];
    const [xj, zj] = polygon[j];
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

function distanceToSegment(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  const t = lengthSq ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / lengthSq)) : 0;
  return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
}

function signedDistance(point, polygon) {
  let best = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    best = Math.min(best, distanceToSegment(point[0], point[1], a[0], a[1], b[0], b[1]));
  }
  return pointInPolygon(point, polygon) ? -best : best;
}

function smoothMin(a, b, k) {
  const h = Math.max(k - Math.abs(a - b), 0) / k;
  return Math.min(a, b) - (h * h * k) / 4;
}

function polygonCenter(polygon) {
  const sum = polygon.reduce((acc, [x, z]) => [acc[0] + x, acc[1] + z], [0, 0]);
  return [sum[0] / polygon.length, sum[1] / polygon.length];
}

function spanningEdges(points) {
  if (points.length < 2) return [];
  const inTree = [0];
  const edges = [];
  while (inTree.length < points.length) {
    let best = null;
    for (const i of inTree) {
      for (let j = 0; j < points.length; j++) {
        if (inTree.includes(j)) continue;
        const d = Math.hypot(points[i][0] - points[j][0], points[i][1] - points[j][1]);
        if (!best || d < best.d) best = { i, j, d };
      }
    }
    inTree.push(best.j);
    edges.push([points[best.i], points[best.j]]);
  }
  return edges;
}

function polygonArea(polygon) {
  let area = 0;
  for (let i = 0; i < polygon.length; i++) {
    const [x1, z1] = polygon[i];
    const [x2, z2] = polygon[(i + 1) % polygon.length];
    area += x1 * z2 - x2 * z1;
  }
  return area / 2;
}

function smoothLoop(points, rounds) {
  let loop = points;
  for (let r = 0; r < rounds; r++) {
    const next = [];
    for (let i = 0; i < loop.length; i++) {
      const p = loop[i];
      const q = loop[(i + 1) % loop.length];
      next.push([p[0] * 0.75 + q[0] * 0.25, p[1] * 0.75 + q[1] * 0.25]);
      next.push([p[0] * 0.25 + q[0] * 0.75, p[1] * 0.25 + q[1] * 0.75]);
    }
    loop = next;
  }
  return loop;
}

function traceLoops(field, nx, nz, x0, z0, step) {
  const value = (i, j) => field[j * nx + i];
  const corner = (i, j) => [x0 + i * step, z0 + j * step];
  const edgePoint = (a, b, va, vb) => {
    const t = va / (va - vb);
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  };
  const points = new Map();
  const links = new Map();
  const link = (a, b) => {
    if (!links.has(a)) links.set(a, []);
    if (!links.has(b)) links.set(b, []);
    links.get(a).push(b);
    links.get(b).push(a);
  };
  for (let j = 0; j < nz - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const v = [value(i, j), value(i + 1, j), value(i + 1, j + 1), value(i, j + 1)];
      const c = [corner(i, j), corner(i + 1, j), corner(i + 1, j + 1), corner(i, j + 1)];
      const code = (v[0] < 0 ? 1 : 0) | (v[1] < 0 ? 2 : 0) | (v[2] < 0 ? 4 : 0) | (v[3] < 0 ? 8 : 0);
      if (code === 0 || code === 15) continue;
      const ids = [`h${i},${j}`, `v${i + 1},${j}`, `h${i},${j + 1}`, `v${i},${j}`];
      const ends = [[0, 1], [1, 2], [3, 2], [0, 3]];
      ids.forEach((id, e) => {
        const [a, b] = ends[e];
        if (!points.has(id) && (v[a] < 0) !== (v[b] < 0)) points.set(id, edgePoint(c[a], c[b], v[a], v[b]));
      });
      const centreInside = (v[0] + v[1] + v[2] + v[3]) / 4 < 0;
      const table = {
        1: [[3, 0]], 2: [[0, 1]], 3: [[3, 1]], 4: [[1, 2]], 6: [[0, 2]], 7: [[3, 2]],
        8: [[2, 3]], 9: [[0, 2]], 11: [[1, 2]], 12: [[1, 3]], 13: [[0, 1]], 14: [[3, 0]],
        5: centreInside ? [[0, 1], [2, 3]] : [[3, 0], [1, 2]],
        10: centreInside ? [[3, 0], [1, 2]] : [[0, 1], [2, 3]],
      };
      for (const [a, b] of table[code]) link(ids[a], ids[b]);
    }
  }
  const visited = new Set();
  const loops = [];
  for (const start of links.keys()) {
    if (visited.has(start)) continue;
    const loop = [];
    let prev = null;
    let current = start;
    while (current && !visited.has(current)) {
      visited.add(current);
      loop.push(points.get(current));
      let next = null;
      for (const n of links.get(current) || []) {
        if (n !== prev && !visited.has(n)) { next = n; break; }
      }
      prev = current;
      current = next;
    }
    if (loop.length >= 3) loops.push(loop);
  }
  return loops;
}

export function shapesGap(aShapes, bShapes) {
  let best = Infinity;
  for (const a of aShapes) for (const b of bShapes) {
    for (const [P, Q] of [[a, b], [b, a]]) {
      for (const p of P) {
        for (let i = 0; i < Q.length; i++) {
          const s0 = Q[i];
          const s1 = Q[(i + 1) % Q.length];
          best = Math.min(best, distanceToSegment(p[0], p[1], s0[0], s0[1], s1[0], s1[1]));
        }
      }
    }
  }
  return best;
}

export function districtBorder(islandPolygons, extraPolygons = [], { offset = 20, bridge = 12, blend = 36, step = 6 } = {}) {
  const islands = (islandPolygons || []).filter((p) => p && p.length >= 3);
  if (!islands.length) return [];
  const extras = (extraPolygons || []).filter((p) => p && p.length >= 3);
  const shapes = [...islands, ...extras];
  const bridges = spanningEdges(islands.map(polygonCenter));

  const all = shapes.flat();
  const margin = offset + blend + step * 3;
  const x0 = Math.min(...all.map((p) => p[0])) - margin;
  const z0 = Math.min(...all.map((p) => p[1])) - margin;
  const nx = Math.ceil((Math.max(...all.map((p) => p[0])) + margin - x0) / step) + 1;
  const nz = Math.ceil((Math.max(...all.map((p) => p[1])) + margin - z0) / step) + 1;

  const boxes = shapes.map((shape) => ({
    shape,
    minX: Math.min(...shape.map((p) => p[0])), maxX: Math.max(...shape.map((p) => p[0])),
    minZ: Math.min(...shape.map((p) => p[1])), maxZ: Math.max(...shape.map((p) => p[1])),
  }));
  const reach = offset + blend;
  const field = new Float64Array(nx * nz);
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const x = x0 + i * step;
      const z = z0 + j * step;
      const near = [];
      let closestBox = Infinity;
      for (const box of boxes) {
        const gap = Math.hypot(Math.max(box.minX - x, 0, x - box.maxX), Math.max(box.minZ - z, 0, z - box.maxZ));
        closestBox = Math.min(closestBox, gap);
        near.push({ box, gap });
      }
      let d = Infinity;
      for (const [a, b] of bridges) {
        const value = distanceToSegment(x, z, a[0], a[1], b[0], b[1]) - bridge;
        d = d === Infinity ? value : smoothMin(d, value, blend);
      }
      if (closestBox > reach && d > reach) {
        field[j * nx + i] = reach;
        continue;
      }
      near.sort((m, n) => m.gap - n.gap);
      for (const { box, gap } of near) {
        if (d !== Infinity && gap - blend > d) break;
        const value = signedDistance([x, z], box.shape);
        d = d === Infinity ? value : smoothMin(d, value, blend);
      }
      field[j * nx + i] = d - offset;
    }
  }

  const loops = traceLoops(field, nx, nz, x0, z0, step);
  if (!loops.length) return [];
  const outer = loops.reduce((a, b) => (Math.abs(polygonArea(b)) > Math.abs(polygonArea(a)) ? b : a));
  return smoothLoop(outer, 3);
}

const COMPACT_ISLAND_GAP_X = 12;
const COMPACT_ISLAND_GAP_Z = 22;
const COMPACT_DISTRICT_PADDING = 12;
const COMPACT_LABEL_SPACE = 22;
const COMPACT_GAP_X_VARIATION = 16;
const COMPACT_GAP_Z_VARIATION = 14;
const COMPACT_ROW_STAGGER = 0.5;
const COMPACT_PACK_VERSION = 11;
const COMPACT_DISTRICT_GAP = 24;
const COMPACT_HAVEN_GAP = 2;
const COMPACT_SEARCH_STEP = 2;
const COMPACT_ANGLE_PENALTY = 400;

function round2(value) {
  return Math.round(value * 100) / 100;
}

function seeded(key) {
  return hashString(key) / 4294967296;
}

function packedRotation() {
  return 0;
}

export function islandNameHalfWidth(island) {
  return Math.min((island?.name || "").length, 26) * 4.2;
}

function islandBounds(island) {
  const rotation = packedRotation(island);
  const polygon = islandPolygon(island?.island_id, { x: 0, z: 0, rotation });
  const xs = polygon.map((p) => p[0]);
  const zs = polygon.map((p) => p[1]);
  const nameHalf = islandNameHalfWidth(island);
  return {
    minX: Math.min(...xs, -nameHalf), maxX: Math.max(...xs, nameHalf),
    minZ: Math.min(...zs), maxZ: Math.max(...zs), rotation,
  };
}

function packDistrict(islands) {
  const cols = Math.max(1, Math.ceil(Math.sqrt(islands.length)));
  const rows = [];
  for (let i = 0; i < islands.length; i += cols) rows.push(islands.slice(i, i + cols));

  const placed = [];
  const rowWidths = [];
  const rowKeys = [];
  let cursorZ = 0;
  let lastRowGap = 0;
  for (const row of rows) {
    const bounds = row.map(islandBounds);
    const depth = Math.max(...bounds.map((b) => b.maxZ - b.minZ));
    const rowTop = cursorZ;
    let cursorX = 0;
    let lastGap = 0;
    const rowItems = [];
    row.forEach((island, k) => {
      const b = bounds[k];
      const free = depth - (b.maxZ - b.minZ);
      rowItems.push({
        island,
        rotation: b.rotation,
        x: cursorX - b.minX,
        z: rowTop - free * seeded(`${island.id}:z`) - b.maxZ,
      });
      lastGap = COMPACT_ISLAND_GAP_X + seeded(`${island.id}:gap`) * COMPACT_GAP_X_VARIATION;
      cursorX += b.maxX - b.minX + lastGap;
    });
    rowWidths.push(cursorX - lastGap);
    rowKeys.push(row[0].id);
    placed.push(rowItems);
    lastRowGap = COMPACT_ISLAND_GAP_Z + seeded(`${row[0].id}:row`) * COMPACT_GAP_Z_VARIATION;
    cursorZ -= depth + lastRowGap;
  }
  const islandWidths = islands.map((i) => { const b = islandBounds(i); return b.maxX - b.minX; });
  const stagger = (islandWidths.reduce((a, w) => a + w, 0) / islandWidths.length) * COMPACT_ROW_STAGGER;
  const staggerFirst = seeded(`${rowKeys[0]}:stagger`) < 0.5;
  const rowStarts = rowWidths.map((_, r) => (r % 2 === (staggerFirst ? 0 : 1) && rows.length > 1 ? stagger : 0));
  const rowSpan = Math.max(...rowWidths.map((w, r) => rowStarts[r] + w));
  const contentWidth = rowSpan;
  const contentDepth = -cursorZ - lastRowGap;

  const offsets = new Map();
  placed.forEach((rowItems, r) => {
    const spare = contentWidth - rowStarts[r] - rowWidths[r];
    const shift = rowStarts[r] + spare * seeded(`${rowKeys[r]}:align`);
    for (const item of rowItems) {
      offsets.set(item.island.id, {
        x: item.x + shift - contentWidth / 2,
        z: item.z + contentDepth / 2 + COMPACT_LABEL_SPACE / 2,
        rotation: item.rotation,
      });
    }
  });
  return {
    width: contentWidth + COMPACT_DISTRICT_PADDING * 2,
    depth: contentDepth + COMPACT_DISTRICT_PADDING * 2 + COMPACT_LABEL_SPACE,
    offsets,
  };
}

function gapToHaven(x, z, width, depth) {
  return Math.hypot(Math.max(Math.abs(x) - width / 2, 0), Math.max(Math.abs(z) - depth / 2, 0));
}

function gapBetween(a, b) {
  return Math.max(
    Math.abs(a.x - b.x) - (a.width + b.width) / 2,
    Math.abs(a.z - b.z) - (a.depth + b.depth) / 2
  );
}

function insideBounds(rect, bounds) {
  if (!bounds) return true;
  return rect.x - rect.width / 2 >= bounds.minX && rect.x + rect.width / 2 <= bounds.maxX &&
    rect.z - rect.depth / 2 >= bounds.minZ && rect.z + rect.depth / 2 <= bounds.maxZ;
}

function nearestSpotOnRay(size, angle, placed, havenClearance, districtGap, keepInBounds) {
  const dirX = Math.cos(angle);
  const dirZ = Math.sin(angle);
  for (let distance = 0; distance <= 4000; distance += COMPACT_SEARCH_STEP) {
    const rect = { x: dirX * distance, z: dirZ * distance, width: size.width, depth: size.depth };
    if (keepInBounds && !insideBounds(rect, MAP_RULES.mapBounds)) return null;
    if (gapToHaven(rect.x, rect.z, rect.width, rect.depth) < havenClearance) continue;
    if (placed.some((other) => gapBetween(rect, other) < districtGap)) continue;
    return { rect, distance };
  }
  return null;
}

const PLACEMENT_ATTEMPTS = [
  { angleSteps: 36, districtGap: COMPACT_DISTRICT_GAP, keepInBounds: true },
  { angleSteps: 144, districtGap: COMPACT_DISTRICT_GAP, keepInBounds: true },
  { angleSteps: 144, districtGap: (MAP_RULES.districtSpacing || 0) + 5, keepInBounds: true },
  { angleSteps: 144, districtGap: (MAP_RULES.districtSpacing || 0) + 5, keepInBounds: false },
];

function placeAroundHaven(size, angle, placed) {
  const havenClearance = (MAP_RULES.havenExclusionRadius || 0) + COMPACT_HAVEN_GAP;
  for (const attempt of PLACEMENT_ATTEMPTS) {
    let best = null;
    for (let step = 0; step <= attempt.angleSteps; step++) {
      for (const sign of step ? [1, -1] : [1]) {
        const deviation = (sign * step * Math.PI) / attempt.angleSteps;
        const spot = nearestSpotOnRay(size, angle + deviation, placed, havenClearance, attempt.districtGap, attempt.keepInBounds);
        if (!spot) continue;
        const score = spot.distance + Math.abs(deviation) * COMPACT_ANGLE_PENALTY;
        if (!best || score < best.score) best = { rect: spot.rect, score };
      }
    }
    if (best) return best.rect;
  }
  return null;
}

function directionsAroundHaven(districts) {
  const angles = new Map();
  for (const district of districts) {
    const l = district.layout;
    if (isValidDistrictLayout(l) && (l.x || l.z)) angles.set(district.id, Math.atan2(l.z, l.x));
  }
  const pending = districts.filter((d) => !angles.has(d.id));
  if (!angles.size) {
    pending.forEach((d, i) => angles.set(d.id, Math.PI / 4 - (i * Math.PI * 2) / pending.length));
    return angles;
  }
  for (const district of pending) {
    const taken = [...angles.values()].map((a) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)).sort((x, y) => x - y);
    let bestStart = taken[taken.length - 1];
    let bestGap = taken[0] + Math.PI * 2 - bestStart;
    for (let i = 1; i < taken.length; i++) {
      if (taken[i] - taken[i - 1] > bestGap) {
        bestGap = taken[i] - taken[i - 1];
        bestStart = taken[i - 1];
      }
    }
    angles.set(district.id, bestStart + bestGap / 2);
  }
  return angles;
}

export function compactWorldLayout(districts) {
  const list = districts || [];
  const withIslands = list.filter((d) => (d?.islands || []).length);
  if (!withIslands.length) return list;

  const angles = directionsAroundHaven(withIslands);
  const placed = [];
  const layoutById = new Map();
  withIslands.forEach((district) => {
    const packed = packDistrict(district.islands);
    const rect = placeAroundHaven(packed, angles.get(district.id), placed);
    if (!rect) return;
    placed.push(rect);
    layoutById.set(district.id, { rect, offsets: packed.offsets });
  });

  return list.map((district) => {
    const entry = layoutById.get(district.id);
    if (!entry) return district;
    const { rect, offsets } = entry;
    return {
      ...district,
      layout: {
        x: round2(rect.x), z: round2(rect.z), width: round2(rect.width), depth: round2(rect.depth),
        packedFor: packSignature(district),
      },
      islands: district.islands.map((island) => {
        const offset = offsets.get(island.id);
        if (!offset) return island;
        return { ...island, layout: { x: round2(rect.x + offset.x), z: round2(rect.z + offset.z), rotation: offset.rotation } };
      }),
    };
  });
}

function packSignature(district) {
  return `${COMPACT_PACK_VERSION}#` + (district?.islands || [])
    .map((i) => `${i.id}:${i.island_id}:${packedRotation(i)}`)
    .join("|");
}

export function needsCompaction(districts) {
  return (districts || []).some((d) =>
    (d?.islands || []).length &&
    (d.layout?.packedFor !== packSignature(d) || (d.islands || []).some((i) => !isValidIslandLayout(i.layout)))
  );
}

export function sameWorldLayout(a, b) {
  const pick = (districts) => JSON.stringify((districts || []).map((d) => [
    d.id, d.layout || null, (d.islands || []).map((i) => [i.id, i.layout || null]),
  ]));
  return pick(a) === pick(b);
}
